// frontend/src/context/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import * as authApi from "../api/auth";
import * as orgsApi from "../api/orgs";
import { setAccessToken } from "../api/client";

/**
 * Shape of the value provided by AuthContext.
 * @typedef {object} AuthContextValue
 * @property {object|null} user       - Authenticated admin object, or null
 * @property {string|null} token      - JWT access token, or null
 * @property {object|null} currentOrg - Active org {id, name, slug, role, ...}, or null
 * @property {Array}       orgs       - Every org the user belongs to (switcher list)
 * @property {boolean}     loading    - True while the initial /me fetch is in flight
 * @property {Function}    login      - (email, password) => Promise<void>
 * @property {Function}    logout     - () => void
 * @property {Function}    switchOrg  - (orgId) => Promise<void>
 */

const AuthContext = createContext(null);

/**
 * AuthProvider — wraps the app and exposes auth state + actions.
 *
 * The access token lives in memory only (React state + the api client), never
 * localStorage. On a page reload that memory is gone, so on mount we try to
 * recover the session from the HttpOnly refresh cookie via /api/auth/refresh.
 *
 * @param {{ children: React.ReactNode }} props
 */
export function AuthProvider({ children }) {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [orgs, setOrgs] = useState([]);
  // True when the session was entered via an admin impersonation token.
  const [impersonating, setImpersonating] = useState(false);
  // Always start in "loading" — every load attempts a silent refresh first.
  const [loading, setLoading] = useState(true);

  /**
   * Load the active-org context (current org + the list for the switcher).
   *
   * Best-effort: a failure (e.g. a user with no org) just clears the org state
   * rather than breaking auth.
   */
  const loadOrgContext = useCallback(async () => {
    try {
      const [org, orgList] = await Promise.all([
        orgsApi.getCurrentOrg(),
        orgsApi.listOrgs(),
      ]);
      setCurrentOrg(org);
      setOrgs(orgList);
    } catch {
      setCurrentOrg(null);
      setOrgs([]);
    }
  }, []);

  /**
   * On mount, recover the session from the refresh cookie: refresh → access
   * token → /me → org context. If there is no valid cookie the refresh fails
   * and we settle as logged-out (ProtectedRoute then redirects to /login).
   */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // Impersonation entry: the admin panel opens the main app with a
        // short-lived token in ?impersonation_token=. Use it directly — there
        // is no refresh cookie for an impersonation session — then strip it
        // from the URL so it isn't bookmarked or leaked.
        const params = new URLSearchParams(window.location.search);
        const impToken = params.get("impersonation_token");
        if (impToken) {
          setAccessToken(impToken);
          setToken(impToken);
          setImpersonating(true);
          params.delete("impersonation_token");
          const qs = params.toString();
          window.history.replaceState(
            {},
            "",
            window.location.pathname + (qs ? `?${qs}` : ""),
          );
          const me = await authApi.getMe();
          if (cancelled) return;
          setUser(me);
          await loadOrgContext();
          return;
        }

        const accessToken = await authApi.refreshToken();
        if (cancelled) return;
        setToken(accessToken);
        const me = await authApi.getMe();
        if (cancelled) return;
        setUser(me);
        await loadOrgContext();
      } catch {
        if (!cancelled) {
          setToken(null);
          setUser(null);
          setCurrentOrg(null);
          setOrgs([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadOrgContext]);

  /**
   * Apply a freshly issued session: store the access token in memory (state +
   * api client) and load the user + org context. Shared by the password-only
   * login path and the MFA challenge path.
   *
   * @param {{access_token: string}} data
   */
  const applySession = useCallback(async (data) => {
    setAccessToken(data.access_token); // hand the token to the api client
    setToken(data.access_token);

    const me = await authApi.getMe();
    setUser(me);
    await loadOrgContext();
  }, [loadOrgContext]);

  /**
   * Authenticate with email + password.
   *
   * When the account has MFA enabled the backend returns no session — just a
   * short-lived ``mfa_token``. In that case we do NOT establish a session and
   * return ``{ mfaRequired: true, mfaToken }`` so the caller can route to the
   * challenge screen. Otherwise the session is applied and ``mfaRequired`` is
   * false.
   *
   * @param {string} email
   * @param {string} password
   * @returns {Promise<{mfaRequired: boolean, mfaToken?: string}>}
   */
  const login = useCallback(async (email, password) => {
    const data = await authApi.login(email, password);
    if (data?.mfa_required) {
      return { mfaRequired: true, mfaToken: data.mfa_token };
    }
    await applySession(data);
    return { mfaRequired: false };
  }, [applySession]);

  /**
   * Complete an MFA-gated login with a TOTP or backup code, establishing the
   * session on success.
   *
   * @param {string} mfaToken short-lived token from the login response
   * @param {string} code TOTP code or one-time backup code
   */
  const completeMfa = useCallback(async (mfaToken, code) => {
    const data = await authApi.mfaChallenge(mfaToken, code);
    await applySession(data);
  }, [applySession]);

  /**
   * Switch the active organisation.
   *
   * Swaps the access token (now carrying the new `org_id` claim) and reloads the
   * org context so `currentOrg`/`orgs` reflect the new active org + role.
   *
   * @param {string} orgId target organisation UUID
   */
  const switchOrg = useCallback(async (orgId) => {
    const newToken = await authApi.switchOrg(orgId);
    setToken(newToken);
    await loadOrgContext();
  }, [loadOrgContext]);

  /**
   * Log out: clear local React state, then revoke the session server-side.
   *
   * Delegates to ``authApi.logout`` which POSTs /api/auth/logout (blacklists the
   * access token + invalidates the refresh family + clears cookies), removes the
   * stored token, and redirects to /login.
   */
  const logout = useCallback(async () => {
    setToken(null);
    setUser(null);
    setCurrentOrg(null);
    setOrgs([]);
    await authApi.logout();
  }, []);

  /**
   * Update editable profile fields (e.g. display_name) and refresh local state.
   *
   * @param {{ display_name: string|null }} fields
   * @returns {Promise<object>} The updated user
   */
  const updateProfile = useCallback(async (fields) => {
    const updated = await authApi.updateMe(fields);
    setUser(updated);
    return updated;
  }, []);

  const value = {
    user,
    token,
    currentOrg,
    orgs,
    loading,
    impersonating,
    login,
    completeMfa,
    logout,
    switchOrg,
    reloadOrgs: loadOrgContext,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook — consume auth context.
 * Throws if used outside of <AuthProvider>.
 *
 * @returns {AuthContextValue}
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}

export default AuthContext;
